<template>
  <base-modal
    :title="t('share')"
    :is-opened="props.isOpened"
    :class="[
      'share-modal',
      { 'share-modal--expert-advice': props.type === 'expertAdvice' },
    ]"
    @close="close"
  >
    <div class="share-modal__container">
      <div v-if="props.type === 'expertAdvice'" class="share-modal__info">
        <div class="share-modal__info-url">{{ infoApp.url }}</div>
        <div class="share-modal__info-title">{{ infoApp.title }}</div>
        <div class="share-modal__info-text">{{ infoApp.text }}</div>
      </div>
      <div v-else-if="props.type === 'course'" class="share-modal__info">
        <div class="share-modal__info-title">{{ props.course?.title }}</div>
      </div>
      <div v-else-if="props.type === 'test'" class="share-modal__info">
        <div class="share-modal__info-title">{{ props.test?.title }}</div>
      </div>
      <div v-else class="share-modal__publication">
        <div
          class="share-modal__publication-title"
          v-html="props.publication?.title"
        />
        <div class="share-modal__publication-footer">
          <div class="share-modal__publication-footer-item">
            <img
              class="share-modal__publication-footer-icon"
              src="/assets/icon/up-icon.svg"
              alt=""
            />
            <div class="share-modal__publication-footer-value">
              {{ props.publication?.reactionsCount || 0 }}
            </div>
          </div>
          <div class="share-modal__publication-footer-item">
            <ion-icon
              class="share-modal__publication-footer-icon"
              :icon="chatboxOutline"
            />
            <div class="share-modal__publication-footer-value">
              {{ props.publication?.commentsCount || 0 }}
            </div>
          </div>
        </div>
      </div>
      <div class="share-modal__divider" />
      <div class="share-modal__soc-networks">
        <share-network
          v-for="network in socNetworks"
          :key="network.image"
          :network="network.type"
          :url="link"
          :title="shareTitle"
          class="share-modal__soc-network-button"
        >
          <img :src="`/assets/icon/${network.image}`" alt="" />
        </share-network>
      </div>
      <button
        type="button"
        class="share-modal__button share-modal__button--apps"
        @click="shareToApps"
      >
        <ion-icon class="publication__footer-icon" :icon="shareSocialOutline" />
        {{ t('shareToApps') }}
      </button>
      <button
        type="button"
        class="share-modal__button share-modal__button--link"
        @click="copyLink"
      >
        <img src="/assets/icon/copy-link-icon.svg" alt="" />
        {{ isCopied ? t('copied') : t('copyLink') }}
      </button>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import { ref, computed, withDefaults } from 'vue';
import {
  CourseI,
  FeedPublicationI,
  LiteFeedPublicationI,
  TestItemI,
} from '@/types';
import BaseModal from '@/components/modals/BaseModal.vue';
import { IonIcon } from '@ionic/vue';
import { chatboxOutline, shareSocialOutline } from 'ionicons/icons';
import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';
import { useInternalization } from '@/composables/useInternalization';

interface Props {
  publication?: FeedPublicationI | LiteFeedPublicationI;
  course?: CourseI;
  test?: TestItemI;
  type?: 'post' | 'course' | 'test' | 'expertAdvice';
  isOpened: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'post',
});
const emit = defineEmits(['close']);

const { t } = useInternalization();

const urlMap = computed(() => ({
  post: `/posts/${props.publication?.publicationId}`,
  course: `/therapy/courses/${props.course?.id}`,
  test: `/therapy-test-view/${props.test?.id}`,
  expertAdvice: '',
}));

const socNetworks = [
  { type: 'whatsapp', image: 'whatsapp.webp' },
  { type: 'twitter', image: 'twitter.svg' },
  { type: 'email', image: 'mail.svg' },
];
const infoApp = {
  url: 'https://www.feelyou.com',
  title:
    "Let's break the stigma surrounding mental health and help those in need.",
  text: 'Check out FeelYou, a free platform that offers support and connects people with similar mental health struggles.',
};
const link = computed(
  () => 'https://app.feelyou.com' + `${urlMap.value[props.type]}`
);
const isCopied = ref(false);

const shareTitle = computed(() => {
  const type = props.type;
  if (type === 'course') {
    return props.course?.title;
  } else if (type === 'post') {
    return props.publication?.title;
  } else if (type === 'test') {
    return props.test?.title;
  } else {
    return props.publication?.title || '';
  }
});

function shareToApps() {
  if (props.type === 'expertAdvice') {
    Share.share(infoApp);
  } else if (props.type === 'course') {
    Share.share({
      url: link.value,
      text: props.course?.title || '',
    });
  } else if (props.type === 'post') {
    Share.share({
      url: link.value,
      text: props.publication?.title || '',
    });
  } else if (props.type === 'test') {
    Share.share({
      url: link.value,
      text: props.test?.title || '',
    });
  }
}

function copyLink() {
  if (props.type === 'expertAdvice') {
    Clipboard.write({
      string: `${infoApp.url} ${infoApp.title} ${infoApp.text}`,
    });
  } else {
    Clipboard.write({
      string: `${link.value}`,
    });
  }
  isCopied.value = true;
}

function close() {
  isCopied.value = false;
  emit('close');
}
</script>

<style lang="scss" scoped>
.share-modal {
  &__container {
    width: 100%;
    height: 100%;
  }

  &__info-url {
    color: #6289ff;
  }

  &__info-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;

    &:not(:first-child) {
      margin: 8px 0;
    }
  }

  &__publication {
    width: 100%;
    border: 1px solid #ebedfb;
    border-radius: 10px;
    padding: 16px;
  }

  &__publication-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #172659;
  }

  &__publication-footer {
    display: flex;
    align-items: center;
    margin-top: 5px;
  }

  &__publication-footer-item {
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-right: 8px;
    }
  }

  &__publication-footer-icon {
    margin-right: 5px;
  }

  &__divider {
    width: 100%;
    height: 1px;
    background-color: #ebedfb;
    margin: 16px 0;
  }

  &__button {
    display: flex;
    align-items: center;
    background: none;
    font-size: 15px;
    line-height: 20px;
    color: #172659;

    &:not(:last-child) {
      margin-bottom: 10px;
    }

    ion-icon,
    img {
      margin-right: 10px;
    }
  }

  &__soc-networks {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  &__soc-network-button {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin-right: 8px;

    img {
      max-width: 100%;
      border-radius: 50%;
    }
  }
}

html.plt-desktop .share-modal__button--apps {
  display: none;
}

html:not(.plt-desktop) .share-modal__soc-networks {
  display: none;
}
</style>
